import React from 'react';
import { Card, CardContent, Typography, Box, Chip, Button } from '@mui/material';
import Link from 'next/link';

interface FeatureCardProps {
  feature: {
    id: string;
    title: string;
    description: string;
    icon: React.ElementType; // Using ElementType for MUI Icons
    category: string;
    status: 'available' | 'premium' | 'coming_soon';
    learnMoreLink: string;
    teaserText: string | null;
  };
}

export const FeatureCard: React.FC<FeatureCardProps> = ({ feature }) => {
  const IconComponent = feature.icon;

  return (
    <Card 
      sx={{
        height: '100%', 
        width: '100%',
        display: 'flex', 
        flexDirection: 'column',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
        <Box sx={{
          mb: 2,
          '& .MuiSvgIcon-root': {
            fontSize: '3rem',
            color: 'primary.main'
          }
        }}>
          <IconComponent data-testid={feature.title.replace(/\s+/g, '-') + '-icon'} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
          {feature.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {feature.description}
        </Typography>
        {feature.status === 'premium' && (
          <Chip 
            label="Premium" 
            color="warning" 
            size="small" 
            sx={{ mb: 1, backgroundColor: 'warning.light', color: 'warning.contrastText' }}
          />
        )}
        {feature.status === 'coming_soon' && (
          <Chip 
            label="Coming Soon" 
            color="info" 
            size="small" 
            sx={{ mb: 1, backgroundColor: 'info.light', color: 'info.contrastText' }}
          />
        )}
      </CardContent>
      <Box sx={{ p: 2, pt: 0, textAlign: 'center' }}>
        {feature.status === 'available' && (
          <Link href={feature.learnMoreLink} prefetch={false}>
            <Button
              component="span"
              variant="outlined"
              size="small"
              sx={{
                mt: 'auto',
                borderColor: 'text.secondary',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                }
              }}
            >
              Learn More
            </Button>
          </Link>
        )}
        {feature.status === 'premium' && (
          <Link href="/pricing" prefetch={false}>
            <Button
              component="span"
              variant="contained"
              size="small"
              color="primary"
              sx={{ mt: 'auto' }}
            >
              Upgrade to Premium
            </Button>
          </Link>
        )}
        {feature.status === 'coming_soon' && feature.teaserText && (
          <Link href="/contact?interest=" prefetch={false}>
            <Button
              component="span"
              variant="outlined"
              size="small"
              color="info"
              sx={{
                mt: 'auto',
                borderColor: 'info.main',
                color: 'info.main',
                '&:hover': {
                  borderColor: 'info.dark',
                  color: 'info.dark',
                }
              }}
            >
              Notify Me
            </Button>
          </Link>
        )}
      </Box>
    </Card>
  );
}; 